import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { invalidateApiCache } from '@/lib/api'

export type UserRoleType =
  | 'saas_admin'
  | 'escola_admin'
  | 'instrutor'
  | 'aluno'
  | 'dgtr_admin'
  | 'dgtr_inspector'
  | 'dgtr_examiner'
  | 'policia'
  | 'policia_admin'

export interface AuthUser {
  id: string
  firstName: string
  lastName: string
  email?: string
  phone?: string
  role: UserRoleType
  /** Todos os papéis que este utilizador pode assumir (multi-role) */
  roles: UserRoleType[]
  schoolId?: string
  permissions: string[]
}

interface OriginalAuth {
  user: AuthUser
  accessToken: string
  refreshToken: string
  tenantSlug: string | null
}

interface AuthState {
  user: AuthUser | null
  accessToken: string | null
  refreshToken: string | null
  tenantSlug: string | null
  isAuthenticated: boolean
  isImpersonating: boolean
  mustChangePassword: boolean
  originalAuth: OriginalAuth | null

  login: (data: {
    user: AuthUser
    accessToken: string
    refreshToken: string
    tenantSlug?: string
    mustChangePassword?: boolean
  }) => void
  logout: () => void
  updateUser: (user: Partial<AuthUser>) => void
  clearMustChangePassword: () => void
  hasPermission: (permission: string) => boolean
  isRole: (...roles: string[]) => boolean
  /** Troca o papel activo (requer re-login via /auth/switch-role) */
  setActiveRole: (role: UserRoleType, accessToken: string, refreshToken: string) => void
  impersonate: (data: {
    user: AuthUser
    accessToken: string
    refreshToken: string
    tenantSlug?: string
  }) => void
  stopImpersonating: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      tenantSlug: null,
      isAuthenticated: false,
      isImpersonating: false,
      mustChangePassword: false,
      originalAuth: null,

      login: ({ user, accessToken, refreshToken, tenantSlug, mustChangePassword }) => {
        // Garantir que roles[] está sempre preenchido
        const normalizedUser: AuthUser = {
          ...user,
          roles: user.roles?.length ? user.roles : [user.role],
        }
        localStorage.setItem('strada_access_token', accessToken)
        localStorage.setItem('strada_refresh_token', refreshToken)
        if (tenantSlug) {
          localStorage.setItem('strada_tenant_slug', tenantSlug)
        }
        invalidateApiCache()
        set({ user: normalizedUser, accessToken, refreshToken, tenantSlug: tenantSlug || null, isAuthenticated: true, mustChangePassword: mustChangePassword ?? false })
      },

      logout: () => {
        localStorage.removeItem('strada_access_token')
        localStorage.removeItem('strada_refresh_token')
        localStorage.removeItem('strada_tenant_slug')
        invalidateApiCache()
        set({
          user: null, accessToken: null, refreshToken: null,
          tenantSlug: null, isAuthenticated: false,
          isImpersonating: false, originalAuth: null,
        })
      },

      updateUser: (partial) => {
        const current = get().user
        if (current) set({ user: { ...current, ...partial } })
      },

      clearMustChangePassword: () => set({ mustChangePassword: false }),

      hasPermission: (permission) => {
        const { user } = get()
        if (!user) return false
        if (user.role === 'saas_admin' || user.role === 'escola_admin' ||
            user.role === 'dgtr_admin') return true
        return user.permissions.includes(permission)
      },

      isRole: (...roles) => {
        const { user } = get()
        if (!user) return false
        return roles.includes(user.role)
      },

      setActiveRole: (role, accessToken, refreshToken) => {
        const current = get()
        if (!current.user) return
        const updatedUser: AuthUser = { ...current.user, role, roles: current.user.roles }
        localStorage.setItem('strada_access_token', accessToken)
        localStorage.setItem('strada_refresh_token', refreshToken)
        invalidateApiCache()
        set({ user: updatedUser, accessToken, refreshToken })
      },

      impersonate: ({ user, accessToken, refreshToken, tenantSlug }) => {
        const current = get()
        // Guarda a sessão original do saas_admin
        const original: OriginalAuth = {
          user: current.user!,
          accessToken: current.accessToken!,
          refreshToken: current.refreshToken!,
          tenantSlug: current.tenantSlug,
        }
        localStorage.setItem('strada_access_token', accessToken)
        localStorage.setItem('strada_refresh_token', refreshToken)
        if (tenantSlug) {
          localStorage.setItem('strada_tenant_slug', tenantSlug)
        } else {
          localStorage.removeItem('strada_tenant_slug')
        }
        invalidateApiCache()
        set({
          user,
          accessToken,
          refreshToken,
          tenantSlug: tenantSlug || null,
          isAuthenticated: true,
          isImpersonating: true,
          originalAuth: original,
        })
      },

      stopImpersonating: () => {
        const { originalAuth } = get()
        if (!originalAuth) return
        localStorage.setItem('strada_access_token', originalAuth.accessToken)
        localStorage.setItem('strada_refresh_token', originalAuth.refreshToken)
        if (originalAuth.tenantSlug) {
          localStorage.setItem('strada_tenant_slug', originalAuth.tenantSlug)
        } else {
          localStorage.removeItem('strada_tenant_slug')
        }
        invalidateApiCache()
        set({
          user: originalAuth.user,
          accessToken: originalAuth.accessToken,
          refreshToken: originalAuth.refreshToken,
          tenantSlug: originalAuth.tenantSlug,
          isAuthenticated: true,
          isImpersonating: false,
          originalAuth: null,
        })
      },
    }),
    {
      name: 'strada-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        tenantSlug: state.tenantSlug,
        isAuthenticated: state.isAuthenticated,
        isImpersonating: state.isImpersonating,
        mustChangePassword: state.mustChangePassword,
        originalAuth: state.originalAuth,
        // nota: setActiveRole não é persistido — é uma função
      }),
    }
  )
)
